import { drawFrame } from '../canvasUtils';

function initFlock(w, h, params) {
    const boids = [];
    for (let i = 0; i < params.count; i++) {
        boids.push({
            x: Math.random() * w,
            y: Math.random() * h,
            vx: (Math.random() - 0.5) * params.maxSpeed,
            vy: (Math.random() - 0.5) * params.maxSpeed,
        });
    }
    return { boids, t: 0 };
}

function wrap(value, max) {
    if (value < 0) return value + max;
    if (value >= max) return value - max;
    return value;
}

function dist2(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return dx * dx + dy * dy;
}

function stepFlock(state, params, w, h) {
    const { boids } = state;
    const r2 = params.perception * params.perception;

    boids.forEach((b, i) => {
        let alignX = 0;
        let alignY = 0;
        let cohX = 0;
        let cohY = 0;
        let sepX = 0;
        let sepY = 0;
        let neighbors = 0;

        boids.forEach((other, j) => {
            if (i === j) return;
            if (dist2(b, other) > r2) return;
            neighbors += 1;
            alignX += other.vx;
            alignY += other.vy;
            cohX += other.x;
            cohY += other.y;
            const dx = b.x - other.x;
            const dy = b.y - other.y;
            const d = Math.sqrt(dx * dx + dy * dy) + 0.001;
            sepX += dx / (d * d);
            sepY += dy / (d * d);
        });

        if (neighbors > 0) {
            alignX = (alignX / neighbors - b.vx) * params.alignment;
            alignY = (alignY / neighbors - b.vy) * params.alignment;
            cohX = ((cohX / neighbors) - b.x) * params.cohesion;
            cohY = ((cohY / neighbors) - b.y) * params.cohesion;
            sepX *= params.separation;
            sepY *= params.separation;
        }

        b.vx += alignX + cohX + sepX;
        b.vy += alignY + cohY + sepY;

        const speed = Math.hypot(b.vx, b.vy);
        if (speed > params.maxSpeed) {
            b.vx = (b.vx / speed) * params.maxSpeed;
            b.vy = (b.vy / speed) * params.maxSpeed;
        }

        b.x = wrap(b.x + b.vx, w);
        b.y = wrap(b.y + b.vy, h);
    });

    state.t += 1;
}

export const flockingBoids = {
    init: (w, h, params) => initFlock(w, h, params),
    step: (ctx, w, h, state, params) => {
        for (let i = 0; i < 2; i++) stepFlock(state, params, w, h);

        const area = drawFrame(ctx, w, h, {
            xLabel: 'x (space)',
            yLabel: 'y (space)',
            title: `Flocking | agents ${state.boids.length}`,
            t: state.t,
        });

        state.boids.forEach((b) => {
            const px = area.left + (b.x / w) * area.width;
            const py = area.top + (b.y / h) * area.height;
            const angle = Math.atan2(b.vy, b.vx);
            ctx.save();
            ctx.translate(px, py);
            ctx.rotate(angle);
            ctx.fillStyle = '#2cbb5d';
            ctx.beginPath();
            ctx.moveTo(6, 0);
            ctx.lineTo(-4, 3);
            ctx.lineTo(-4, -3);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        });
    },
};

export const flockingPredators = {
    init: (w, h, params) => {
        const state = initFlock(w, h, params);
        state.predators = [];
        for (let i = 0; i < params.predatorCount; i++) {
            state.predators.push({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * params.predatorSpeed,
                vy: (Math.random() - 0.5) * params.predatorSpeed,
            });
        }
        return state;
    },
    step: (ctx, w, h, state, params) => {
        stepFlock(state, params, w, h);

        state.predators.forEach((p) => {
            let nearest = null;
            let best = Infinity;
            state.boids.forEach((b) => {
                const d = dist2(p, b);
                if (d < best) {
                    best = d;
                    nearest = b;
                }
            });
            if (nearest) {
                const dx = nearest.x - p.x;
                const dy = nearest.y - p.y;
                const d = Math.hypot(dx, dy) + 0.001;
                p.vx += (dx / d) * 0.4;
                p.vy += (dy / d) * 0.4;
            }
            const speed = Math.hypot(p.vx, p.vy);
            if (speed > params.predatorSpeed) {
                p.vx = (p.vx / speed) * params.predatorSpeed;
                p.vy = (p.vy / speed) * params.predatorSpeed;
            }
            p.x = wrap(p.x + p.vx, w);
            p.y = wrap(p.y + p.vy, h);
        });

        const area = drawFrame(ctx, w, h, {
            xLabel: 'x (space)',
            yLabel: 'y (space)',
            title: `Predator-prey flocking`,
            t: state.t,
        });

        state.boids.forEach((b) => {
            const px = area.left + (b.x / w) * area.width;
            const py = area.top + (b.y / h) * area.height;
            ctx.fillStyle = '#4fc3f7';
            ctx.beginPath();
            ctx.arc(px, py, 2.5, 0, Math.PI * 2);
            ctx.fill();
        });

        state.predators.forEach((p) => {
            const px = area.left + (p.x / w) * area.width;
            const py = area.top + (p.y / h) * area.height;
            ctx.fillStyle = '#ef4743';
            ctx.beginPath();
            ctx.arc(px, py, 4, 0, Math.PI * 2);
            ctx.fill();
        });
    },
};
